import React, { useState, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import Chart from 'react-apexcharts';

import {
    useGetAggregatedPromoterDataQuery,
    useGetAggregatedSGPDataQuery,
    useGetDistributorSubscribersDataQuery,
    useGetInvestmentFundsQuery,
} from 'api/main';
import { texts } from 'utils/languages/fr';
import { roleCategories, subscriptionStatus } from 'utils/constants';
import { parseSum } from 'utils/helpers';
import {
    customTotalParser,
    parseCustomData,
    pieChartOptions,
    subscriptionNumberFormatter,
} from '../admin-distributors/AdminDistributorDashboard';

import { InfoCard, DashboardSection, ChartHeader, ChartContainer } from '../common-components';
import SubscriptionsByPartner from './SubscriptionsByPartner';
import { CustomSelect, Loader } from 'components';
import { Typography, Box, MenuItem, Grid } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { useUserRoleMatchCategory } from '../../../utils/hooks';

const useStyles = makeStyles(theme => ({
    filterInput: {
        width: 250,
        marginBottom: theme.spacing(3),
    },
    whiteBackground: {
        background: theme.palette.common.white,
    },
    infoContainer: {
        marginBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    subtitle: {
        paddingLeft: theme.spacing(1),
        fontWeight: 600,
    },
    subsection: {
        paddingLeft: theme.spacing(3),
    },
}));

const CompletedSection = props => {
    const { onListFilter, userIsNetworkAdmin, userIsProvider } = props;
    const classes = useStyles();

    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
    });
    const { data: aggregatedSGPData = null, isFetching: sgpDataLoader } =
        useGetAggregatedSGPDataQuery(undefined, { skip: userIsProvider || userIsPromoter });
    const { data: promoterAggregatedData = {}, isFetching: promoterDataLoader } =
        useGetAggregatedPromoterDataQuery({}, { skip: !userIsPromoter });
    const { data: aggregatedData = {}, isFetching: dataLoader } =
        useGetDistributorSubscribersDataQuery({ subscriptionStatus: subscriptionStatus.FINISHED });

    const [selectedFund, setSelectedFund] = useState('all');

    // used for charts that show data by fund
    const filteredFundList = useMemo(
        () => fundList.filter(f => !!aggregatedData?.[f.id]),
        [fundList, aggregatedData]
    );

    const totalValueByFund = useMemo(
        () => customTotalParser(aggregatedData, selectedFund, 'sum_total_subscriptions'),
        [aggregatedData, selectedFund]
    );
    const totalNumberByFund = useMemo(
        () => customTotalParser(aggregatedData, selectedFund, 'total_subscriptions'),
        [aggregatedData, selectedFund]
    );

    const chartLabels = useMemo(
        () =>
            selectedFund === 'all'
                ? filteredFundList.map(f => f.name)
                : Object.keys(aggregatedData?.[selectedFund] || {}).map(k => k || '-'),
        [selectedFund, filteredFundList, aggregatedData]
    );

    return (
        <DashboardSection title={texts.COMPLETED}>
            <Loader loading={dataLoader || fundsLoader || !aggregatedData} marginTop={10}>
                <Box className={classes.infoContainer}>
                    <Typography variant="body1" gutterBottom className={classes.subtitle}>
                        {texts.SUBSCRIPTIONS}
                    </Typography>
                    <Box className={classes.subsection}>
                        <CustomSelect
                            inputStyle={classes.filterInput}
                            classes={{ root: classes.whiteBackground }}
                            value={selectedFund}
                            label={texts.PRODUIT}
                            onChange={e => setSelectedFund(e.target.value)}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {filteredFundList
                                .sort((a, b) => (a.name > b.name ? 1 : -1))
                                .map(f => (
                                    <MenuItem key={f.id} value={f.id}>
                                        {f.name}
                                    </MenuItem>
                                ))}
                        </CustomSelect>
                        {totalValueByFund === 0 && totalNumberByFund === 0 ? (
                            <Typography variant="subtitle1" align="center" gutterBottom>
                                {texts.NO_DATA_AVAILABLE}
                            </Typography>
                        ) : (
                            <>
                                {!userIsNetworkAdmin && (
                                    <Box
                                        marginBottom={3}
                                        display="flex"
                                        justifyContent="space-around"
                                        alignItems="center"
                                    >
                                        <ChartHeader
                                            useSymbol
                                            number={totalValueByFund}
                                            title={texts.VALUE}
                                            handleClick={onListFilter(
                                                {
                                                    product:
                                                        selectedFund === 'all'
                                                            ? undefined
                                                            : [selectedFund],
                                                },
                                                'investors'
                                            )}
                                        />
                                        <ChartHeader
                                            handleClick={onListFilter(
                                                {
                                                    product:
                                                        selectedFund === 'all'
                                                            ? undefined
                                                            : [selectedFund],
                                                },
                                                'investors'
                                            )}
                                            title={texts.NUMBER}
                                            number={totalNumberByFund}
                                        />
                                    </Box>
                                )}
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap="15px"
                                    height="100%"
                                    marginBottom={3}
                                >
                                    {!userIsNetworkAdmin && (
                                        <ChartContainer
                                            subtitleProps={{
                                                selectedFund: selectedFund,
                                                subType: 'direct',
                                                valueType: 'value',
                                            }}
                                            flex="0 1 25%"
                                        >
                                            <Chart
                                                type="pie"
                                                series={parseCustomData(
                                                    aggregatedData,
                                                    selectedFund,
                                                    'sum_direct_subscriptions'
                                                )}
                                                options={{
                                                    ...pieChartOptions,
                                                    tooltip: {
                                                        ...pieChartOptions.tooltip,
                                                        y: { formatter: val => parseSum(val) },
                                                    },
                                                    labels: chartLabels,
                                                }}
                                            />
                                        </ChartContainer>
                                    )}
                                    <ChartContainer
                                        useSymbol
                                        number={totalValueByFund}
                                        title={userIsNetworkAdmin ? texts.VALUE : ''}
                                        handleClick={onListFilter(
                                            {
                                                product:
                                                    selectedFund === 'all'
                                                        ? undefined
                                                        : [selectedFund],
                                            },
                                            'investors'
                                        )}
                                        subtitleProps={{
                                            selectedFund: selectedFund,
                                            subType: 'indirect',
                                            valueType: 'value',
                                        }}
                                        flex={`0 1 ${userIsNetworkAdmin ? 40 : 25}%`}
                                    >
                                        <Chart
                                            type="pie"
                                            series={parseCustomData(
                                                aggregatedData,
                                                selectedFund,
                                                'sum_indirect_subscriptions'
                                            )}
                                            options={{
                                                ...pieChartOptions,
                                                tooltip: {
                                                    ...pieChartOptions.tooltip,
                                                    y: { formatter: val => parseSum(val) },
                                                },
                                                labels: chartLabels,
                                            }}
                                        />
                                    </ChartContainer>
                                    {!userIsNetworkAdmin && (
                                        <ChartContainer
                                            subtitleProps={{
                                                selectedFund: selectedFund,
                                                subType: 'direct',
                                                valueType: 'number',
                                            }}
                                            flex="0 1 25%"
                                        >
                                            <Chart
                                                type="pie"
                                                series={parseCustomData(
                                                    aggregatedData,
                                                    selectedFund,
                                                    'direct_subscriptions'
                                                )}
                                                options={{
                                                    ...pieChartOptions,
                                                    tooltip: {
                                                        ...pieChartOptions.tooltip,
                                                        y: {
                                                            formatter: subscriptionNumberFormatter,
                                                        },
                                                    },
                                                    labels: chartLabels,
                                                }}
                                            />
                                        </ChartContainer>
                                    )}
                                    <ChartContainer
                                        subtitleProps={{
                                            selectedFund: selectedFund,
                                            subType: 'indirect',
                                            valueType: 'number',
                                        }}
                                        handleClick={onListFilter(
                                            {
                                                product:
                                                    selectedFund === 'all'
                                                        ? undefined
                                                        : [selectedFund],
                                            },
                                            'investors'
                                        )}
                                        title={userIsNetworkAdmin ? texts.NUMBER : ''}
                                        number={totalNumberByFund}
                                        flex={`0 1 ${userIsNetworkAdmin ? 40 : 25}%`}
                                    >
                                        <Chart
                                            type="pie"
                                            series={parseCustomData(
                                                aggregatedData,
                                                selectedFund,
                                                'indirect_subscriptions'
                                            )}
                                            options={{
                                                ...pieChartOptions,
                                                tooltip: {
                                                    ...pieChartOptions.tooltip,
                                                    y: {
                                                        formatter: subscriptionNumberFormatter,
                                                    },
                                                },
                                                labels: chartLabels,
                                            }}
                                        />
                                    </ChartContainer>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
                <SubscriptionsByPartner
                    status={subscriptionStatus.FINISHED}
                    classes={classes}
                    onListFilter={onListFilter}
                    type="completed"
                />
                {!userIsNetworkAdmin && !userIsProvider && (
                    <Loader loading={sgpDataLoader || promoterDataLoader}>
                        <Box className={classes.infoContainer}>
                            <Typography variant="body1" gutterBottom className={classes.subtitle}>
                                {texts.PARTNERS}
                            </Typography>
                            <Grid container spacing={2}>
                                {!userIsPromoter && (
                                    <InfoCard
                                        onClick={onListFilter(
                                            { noConventionFullySigned: 1 },
                                            'distributors'
                                        )}
                                        number={
                                            aggregatedSGPData
                                                ? aggregatedSGPData?.nr_distributors_with_zero_signed_conventions
                                                : promoterAggregatedData?.nr_distributors_with_zero_signed_conventions
                                        }
                                        label={texts.TOTAL_PARTNERS}
                                    />
                                )}
                                <InfoCard
                                    onClick={onListFilter(
                                        { oneOrMoreConventionsFullySigned: 1 },
                                        'distributors'
                                    )}
                                    number={
                                        aggregatedSGPData
                                            ? aggregatedSGPData?.nr_distributors_with_min_one_signed_convention
                                            : promoterAggregatedData?.nr_distributors_with_min_one_signed_convention
                                    }
                                    label={texts.AT_LEAST_ONE_CONVENTION_PARTNERS}
                                />
                                <InfoCard
                                    onClick={onListFilter(
                                        { oneOrMoreSubscriptionsFinished: 1 },
                                        'distributors'
                                    )}
                                    number={
                                        aggregatedSGPData
                                            ? aggregatedSGPData?.nr_distributors_with_min_one_finished_subscription
                                            : promoterAggregatedData?.nr_distributors_with_min_one_finished_subscription
                                    }
                                    label={texts.AT_LEAST_ONE_SUBSCRIPTION_PARTNERS}
                                />
                                {!userIsPromoter && (
                                    <InfoCard
                                        onClick={onListFilter(
                                            { oneOrMoreConventionsFullySigned: 1 },
                                            'distributors'
                                        )}
                                        number={
                                            aggregatedSGPData
                                                ? aggregatedSGPData?.nr_conventions_fully_signed
                                                : promoterAggregatedData?.nr_conventions_fully_signed
                                        }
                                        label={texts.SIGNED_CONVENTIONS}
                                    />
                                )}
                            </Grid>
                        </Box>
                    </Loader>
                )}
            </Loader>
        </DashboardSection>
    );
};

CompletedSection.propTypes = {
    onListFilter: PropTypes.func.isRequired,
    userIsNetworkAdmin: PropTypes.bool,
    userIsProvider: PropTypes.bool,
};

export default CompletedSection;
